import { ArrayUtils } from '@colonist/utils';
import { CardDataController } from '@colonist/game-card-types';
import { ModeSettingEnum, TradeEmbargoControllerReader } from '@colonist/game-base-types';
import { CardEnum, CardTypeEnum } from '@colonist/game-states';
import { BankCardUtils } from '@colonist/game-state-types';
import { CardValidatorOrdered } from '../card/CardValidatorOrdered.ts';
import { TradeErrorHighlightLocationEnum } from './TradeTypes.ts';
import { TradeUtils } from './TradeUtils.ts';
export class TradeCreationDataValidator {
    getTradeCreationDataError(tradeCreationData) {
        this.tradeCreationData = tradeCreationData;
        const dataError = this.getTradeDataError();
        if (dataError != undefined) return dataError;
        const offeredResourcesWithoutAnyResourceCard = ArrayUtils.removeAllInstancesOfElementInArray(this.tradeCreationData.offeredResources, CardEnum.AnyResource);
        if (!this.playerCards.hasCardsInInventory(offeredResourcesWithoutAnyResourceCard)) return {
            error: {
                key: 'strings:game.errors.tradeErrors.doesNotHaveOfferedCards'
            }
        };
        if (this.tradeCreationData.isBankTrade) return this.getBankTradeError();
        if (this.numberOfPlayers <= 1) return {
            error: {
                key: 'strings:game.errors.tradeErrors.only1HumanPlayer'
            },
            payload: {
                isPlayerTradeButtonDisabled: true
            }
        };
        const wantedCardOverLimitError = this.getWantedCardOverLimitError();
        if (wantedCardOverLimitError != undefined) return wantedCardOverLimitError;
        const isCounterOffer = this.tradeCreationData.counterOfferInResponseToTradeId != undefined;
        if (isCounterOffer) return this.getCounterOfferTradeError();
        return this.getPlayerTradeError(tradeCreationData.creator);
    }
    getTradeDataError() {
        if (this.tradeCreationData.isBankTrade) {
            if (!this.currentTurnPlayerColors.includes(this.tradeCreationData.creator)) return {
                error: {
                    key: 'strings:game.errors.tradeErrors.bankTradesOnYourTurnOnly'
                },
                payload: {
                    isBankTradeButtonDisabled: true
                }
            };
        }
        const sameTypeOfCardsError = this.getTradeErrorTradeSameTypeCards();
        if (sameTypeOfCardsError != undefined) return sameTypeOfCardsError;
        const emptyTradeError = this.getEmptyTradeError();
        if (emptyTradeError != undefined) return emptyTradeError;
        if (!this.areAllResourcesActiveInGame()) return {
            error: {
                key: 'strings:game.errors.tradeErrors.cardIsNotActiveForGameMode'
            }
        };
        return undefined;
    }
    getBankTradeError() {
        if (TradeUtils.isOpenEndedTrade(this.tradeCreationData.offeredResources, this.tradeCreationData.wantedResources)) return {
            error: {
                key: 'strings:game.errors.tradeErrors.cannotUseAnyResourceCardsWithBank'
            },
            payload: {
                isBankTradeButtonDisabled: true
            }
        };
        const bankTradeRatioError = TradeUtils.getBankTradeRatioError(this.tradeCreationData.offeredResources, this.tradeCreationData.wantedResources, this.playerTradeBankRatioState);
        if (bankTradeRatioError != undefined) return bankTradeRatioError;
        const emptyBankError = this.getEmptyBankError();
        if (emptyBankError != undefined) return emptyBankError;
        return undefined;
    }
    getCounterOfferTradeError() {
        if (this.tradeCreationData.counterOfferInResponseToTradeId == undefined) return {
            error: {
                key: 'strings:game.errors.tradeErrors.invalidCounterOfferId'
            }
        };
        const isOriginalOfferStillOpen = this.activeTradeStates[this.tradeCreationData.counterOfferInResponseToTradeId] != undefined;
        if (!isOriginalOfferStillOpen) return {
            error: {
                key: 'strings:game.errors.tradeErrors.counterOfferOnlyIfOriginalOfferIsStillOpen'
            }
        };
        const tradeExecutor = this.getTradeExecutorFromTradeCreationData();
        const hasTradeExecutorEmbargoedTradeCreator = TradeEmbargoControllerReader.doesPlayer1HaveAnEmbargoAgainstPlayer2(this.embargoState, tradeExecutor, this.tradeCreationData.creator);
        if (hasTradeExecutorEmbargoedTradeCreator) return {
            error: {
                key: 'strings:game.errors.tradeErrors.currentTurnPlayerBlockedTradingWithYou'
            }
        };
        const actualTradeOfferState = this.activeTradeStates[this.tradeCreationData.counterOfferInResponseToTradeId];
        const actualTradeOfferStateCreator = actualTradeOfferState === null || actualTradeOfferState === void 0 ? void 0 : actualTradeOfferState.creator;
        if (actualTradeOfferStateCreator == undefined) return {
            error: {
                key: 'strings:game.errors.tradeErrors.invalidCounterOfferId'
            }
        };
        const identicalTradeId = this.isIdenticalTradeActive(actualTradeOfferStateCreator);
        if (identicalTradeId != undefined) return {
            error: {
                key: 'strings:game.errors.tradeErrors.identicalTradeIsAlreadyActive'
            },
            payload: {
                highlightTrade: {
                    tradeIds: [
                        identicalTradeId
                    ],
                    autoAcceptOffer: true
                }
            }
        };
        /*
         Read this discussion: https://github.com/colonistio/katan/pull/21758#discussion_r2580842683.
         isCounterOffersDisabled check is a solution to prevent counter offer creation in rush gamemode without adding a new state into the TradeState.
         Ideally, the backend should inform the client about when to prevent counter offer creation.
         Don't edit this logic without editing the shouldCounterOfferButtonInitiateRegularTrade logic in UIGameTradeOfferCounterOfferButton.tsx as well.
         */ const isCounterOffersDisabled = this.modeSetting === ModeSettingEnum.ColonistRush4P;
        if (isCounterOffersDisabled) return {
            error: {
                key: 'strings:game.errors.tradeErrors.counterOfferDisabledInColonistRushMode'
            }
        };
        return this.getSharedCounterOfferAndPlayerTradeErrors();
    }
    getPlayerTradeError(creator) {
        if (!this.currentTurnPlayerColors.includes(creator)) return {
            error: {
                key: 'strings:game.errors.tradeErrors.canOnlyInitiateTradeFromYourTurn'
            },
            payload: {
                isPlayerTradeButtonDisabled: true
            }
        };
        if (TradeEmbargoControllerReader.didPlayerEmbargoAllOtherPlayers(this.embargoState, creator)) return {
            error: {
                key: 'strings:game.errors.tradeErrors.youBlockedTradingWithAllPlayers'
            }
        };
        if (TradeEmbargoControllerReader.didEveryoneEmbargoPlayer(this.embargoState, creator)) return {
            error: {
                key: 'strings:game.errors.tradeErrors.everyoneBlockedTradingWithYou'
            }
        };
        const identicalTradeId = this.isIdenticalTradeActive(creator);
        if (identicalTradeId != undefined) return {
            error: {
                key: 'strings:game.errors.tradeErrors.identicalTradeIsAlreadyActive'
            },
            payload: {
                highlightTrade: {
                    tradeIds: [
                        identicalTradeId
                    ]
                }
            }
        };
        return this.getSharedCounterOfferAndPlayerTradeErrors();
    }
    getSharedCounterOfferAndPlayerTradeErrors() {
        const noOneHasWantedResourceError = this.getNoOneHasWantedResourceError();
        if (noOneHasWantedResourceError != undefined) return noOneHasWantedResourceError;
        if (this.hasPlayerCreatedIdenticalTradeTooManyTimes()) {
            return {
                error: {
                    key: 'strings:game.errors.tradeErrors.identicalTradeLimit'
                },
                payload: {
                    highlightCards: {
                        cardsToHighlight: this.allValidCards,
                        locations: [
                            TradeErrorHighlightLocationEnum.ProposalOffered,
                            TradeErrorHighlightLocationEnum.ProposalWanted
                        ]
                    }
                }
            };
        }
        if (this.doesPlayerHaveTooManyActiveTrades()) {
            return {
                error: {
                    key: 'strings:game.errors.tradeErrors.tooManyActiveTrades'
                },
                payload: {
                    highlightTrade: {
                        tradeIds: [
                            ...this.getAllMyActiveTradeIds()
                        ]
                    }
                }
            };
        }
        return undefined;
    }
    getTradeErrorTradeSameTypeCards() {
        const duplicatedResources = [];
        const uniqueOfferedResources = ArrayUtils.removeDuplicatesFromArray(this.tradeCreationData.offeredResources);
        for (const offeredResource of uniqueOfferedResources){
            if (offeredResource == CardEnum.AnyResource) continue;
            if (this.tradeCreationData.wantedResources.includes(offeredResource)) duplicatedResources.push(offeredResource);
        }
        if (duplicatedResources.length >= 1) {
            return {
                error: {
                    key: 'strings:game.errors.tradeErrors.tradingSameTypeCards'
                },
                payload: {
                    highlightCards: {
                        cardsToHighlight: duplicatedResources,
                        locations: [
                            TradeErrorHighlightLocationEnum.ProposalOffered,
                            TradeErrorHighlightLocationEnum.ProposalWanted
                        ]
                    },
                    isBankTradeButtonDisabled: true,
                    isPlayerTradeButtonDisabled: true
                }
            };
        }
        return undefined;
    }
    /**
     * About getTradeExecutorFromTradeCreationData:
    *  * Returning the first element of the current turn player colors array is a bad practice.
    *  * It is fine for now because counter offers are only enabled in games where there is only one current turn player.
    *  * This should be replaced by this.state.counterOfferInResponseToPlayerColor to enable counter offers in games with multiple current turn players.
    */ getTradeExecutorFromTradeCreationData() {
        const isCounterOffer = this.tradeCreationData.counterOfferInResponseToTradeId != undefined;
        if (isCounterOffer) return this.currentTurnPlayerColors[0];
        return this.tradeCreationData.creator;
    }
    getEmptyTradeError() {
        const isOfferedResourcesEmpty = this.isOfferedResourcesEmpty();
        const isWantedResourcesEmpty = this.isWantedResourcesEmpty();
        if (isOfferedResourcesEmpty && isWantedResourcesEmpty) {
            return {
                error: {
                    key: 'strings:game.errors.tradeErrors.offerIncomplete'
                },
                payload: {
                    highlightCards: {
                        cardsToHighlight: this.allValidCards,
                        locations: [
                            TradeErrorHighlightLocationEnum.OfferedSelector,
                            TradeErrorHighlightLocationEnum.WantedSelector
                        ]
                    },
                    isPlayerTradeButtonDisabled: true,
                    isBankTradeButtonDisabled: true
                }
            };
        }
        if (isOfferedResourcesEmpty) {
            return {
                error: {
                    key: 'strings:game.errors.tradeErrors.offerIncomplete'
                },
                payload: {
                    highlightCards: {
                        cardsToHighlight: this.allValidCards,
                        locations: [
                            TradeErrorHighlightLocationEnum.OfferedSelector
                        ]
                    },
                    isPlayerTradeButtonDisabled: true,
                    isBankTradeButtonDisabled: true
                }
            };
        }
        if (isWantedResourcesEmpty) {
            return {
                error: {
                    key: 'strings:game.errors.tradeErrors.offerIncomplete'
                },
                payload: {
                    highlightCards: {
                        cardsToHighlight: this.allValidCards,
                        locations: [
                            TradeErrorHighlightLocationEnum.WantedSelector
                        ]
                    },
                    isPlayerTradeButtonDisabled: true,
                    isBankTradeButtonDisabled: true
                }
            };
        }
        const noCardsToOpenEndedTradeError = this.getNoCardsToOpenEndedTradeError();
        if (noCardsToOpenEndedTradeError) return noCardsToOpenEndedTradeError;
        return undefined;
    }
    getEmptyBankError() {
        const resourcesTheBankDoesNotHave = [];
        const uniqueWantedResources = ArrayUtils.removeDuplicatesFromArray(this.tradeCreationData.wantedResources);
        for (const uniqueWantedResource of uniqueWantedResources){
            const allCardOfType = this.tradeCreationData.wantedResources.filter((card)=>card == uniqueWantedResource);
            if (this.bankHasRequiredResources(allCardOfType)) continue;
            resourcesTheBankDoesNotHave.push(uniqueWantedResource);
        }
        if (resourcesTheBankDoesNotHave.length == 0) return undefined;
        return {
            error: {
                key: 'strings:game.errors.tradeErrors.bankDoesNotHaveRequestedResources'
            },
            payload: {
                highlightCards: {
                    cardsToHighlight: resourcesTheBankDoesNotHave,
                    locations: [
                        TradeErrorHighlightLocationEnum.ProposalWanted
                    ]
                }
            }
        };
    }
    bankHasRequiredResources(resourceCards) {
        const cardInventory = new CardValidatorOrdered(this.bankState.resourceCards);
        return cardInventory.hasCardsInInventory(resourceCards);
    }
    getNoCardsToOpenEndedTradeError() {
        if (!TradeUtils.isOpenEndedTrade(this.tradeCreationData.offeredResources, this.tradeCreationData.wantedResources)) return undefined;
        if (this.playerCards.getNumberOfCardsInInventory() != 0) return undefined;
        return {
            error: {
                key: 'strings:game.errors.tradeErrors.notEnoughCardsForOpenEndedTrade'
            },
            payload: {
                isPlayerTradeButtonDisabled: true,
                isBankTradeButtonDisabled: true
            }
        };
    }
    getWantedCardOverLimitError() {
        const maximumAmountOfCardsThatCanBeRequested = TradeUtils.tradeMaximumWantedResources;
        const isWantedCardOverLimit = this.tradeCreationData.wantedResources.length >= maximumAmountOfCardsThatCanBeRequested;
        if (isWantedCardOverLimit) {
            return {
                error: {
                    key: 'strings:game.errors.tradeErrors.tooManyWantedCards'
                },
                payload: {
                    highlightCards: {
                        cardsToHighlight: this.allValidCards,
                        locations: [
                            TradeErrorHighlightLocationEnum.ProposalWanted
                        ]
                    },
                    isPlayerTradeButtonDisabled: true
                }
            };
        }
        return undefined;
    }
    isOfferedResourcesEmpty() {
        if (this.tradeCreationData.offeredResources.length == 0) return true;
        return false;
    }
    isWantedResourcesEmpty() {
        if (this.tradeCreationData.wantedResources.length == 0) return true;
        return false;
    }
    areAllResourcesActiveInGame() {
        const validCardsToTrade = CardDataController.getCardEnumsOfTypes(this.activeResourceCards);
        validCardsToTrade.push(CardEnum.AnyResource);
        for (const offeredResource of this.tradeCreationData.offeredResources){
            if (!validCardsToTrade.includes(offeredResource)) return false;
        }
        for (const wantedResource of this.tradeCreationData.wantedResources){
            if (!validCardsToTrade.includes(wantedResource)) return false;
        }
        return true;
    }
    getNoOneHasWantedResourceError() {
        const uniqueWantedResources = ArrayUtils.removeDuplicatesFromArray(this.tradeCreationData.wantedResources);
        const resourcesThatNoOneHas = [];
        for (const wantedResource of uniqueWantedResources){
            if (this.doesASinglePlayerHaveResourceCard(wantedResource)) continue;
            resourcesThatNoOneHas.push(wantedResource);
        }
        if (resourcesThatNoOneHas.length > 0) {
            return {
                error: {
                    key: 'strings:game.errors.tradeErrors.noOneHasThatResource'
                },
                payload: {
                    highlightCards: {
                        cardsToHighlight: resourcesThatNoOneHas,
                        locations: [
                            TradeErrorHighlightLocationEnum.ProposalWanted
                        ]
                    }
                }
            };
        }
        return undefined;
    }
    doesASinglePlayerHaveResourceCard(cardEnum) {
        const cardData = CardDataController.getCardDataForCard(cardEnum);
        if (cardData == undefined) return false;
        if (this.bankState.hideBankCards) return true;
        const maximumNumberOfCards = BankCardUtils.resourceCardCountFor(this.modeSetting, cardData.cardTypeEnum);
        const currentCardCount = ArrayUtils.countOccurenceOfValueInArray(this.bankState.resourceCards.cards, cardEnum);
        return currentCardCount != maximumNumberOfCards;
    }
    isIdenticalTradeActive(creator) {
        for (const activeTradeState of Object.values(this.activeTradeStates)){
            if (activeTradeState == null) continue;
            if (activeTradeState.creator != creator) continue;
            if (TradeCreationDataValidator.isIdenticalTrade(activeTradeState, this.tradeCreationData)) return activeTradeState.id;
        }
        return undefined;
    }
    hasPlayerCreatedIdenticalTradeTooManyTimes() {
        const maximumNumberOfTimesTradeCanBeCreated = this.getMaximumNumberOfTimesIdenticalTradeCanBeCreated();
        const numberOfTimesIdenticalTradeWasCreated = this.getNumberOfTimesIdenticalOfferWasCreatedThisTurnByPlayer(this.tradeCreationData);
        return numberOfTimesIdenticalTradeWasCreated >= maximumNumberOfTimesTradeCanBeCreated;
    }
    getMaximumNumberOfTimesIdenticalTradeCanBeCreated() {
        if (this.isTradeCreatorBot) return TradeCreationDataValidator.maximumNumberOfTimesAnIdenticalTradeCanBeCreatedByBots;
        if (!this.currentTurnPlayerColors.includes(this.tradeCreationData.creator)) return TradeCreationDataValidator.maximumNumberOfTimesAnIdenticalTradeCanBeCreatedForOtherPlayers;
        return TradeCreationDataValidator.maximumNumberOfTimesAnIdenticalTradeCanBeCreatedForCurrentTurnPlayer;
    }
    doesPlayerHaveTooManyActiveTrades() {
        const maximumNumberOfActiveTrades = this.currentTurnPlayerColors.includes(this.tradeCreationData.creator) ? TradeCreationDataValidator.maximumNumberOfActiveUniqueTradesForCurrentTurnPlayer : TradeCreationDataValidator.maximumNumberOfActiveUniqueTradesForOtherPlayers;
        const numberOfActiveTrades = this.getNumberOfActiveTradesCreatedByTradeCreator();
        return numberOfActiveTrades >= maximumNumberOfActiveTrades;
    }
    getNumberOfActiveTradesCreatedByTradeCreator() {
        let amount = 0;
        for (const tradeState of Object.values(this.activeTradeStates)){
            if (tradeState == null) continue;
            if (tradeState.creator != this.tradeCreationData.creator) continue;
            amount += 1;
        }
        return amount;
    }
    getAllMyActiveTradeIds() {
        const myTradeIds = [];
        for (const tradeState of Object.values(this.activeTradeStates)){
            if (tradeState == null) continue;
            if (tradeState.creator != this.tradeCreationData.creator) continue;
            myTradeIds.push(tradeState.id);
        }
        return myTradeIds;
    }
    getNumberOfTimesIdenticalOfferWasCreatedThisTurnByPlayer(tradeCreationData) {
        const tradesClosed = this.tradesClosedThisTurn;
        if (tradesClosed == null) return 0;
        let amount = 0;
        for (const tradeStates of Object.values(tradesClosed)){
            if (tradeStates == undefined) continue;
            if (!TradeCreationDataValidator.isIdenticalTrade(tradeStates, tradeCreationData)) continue;
            amount += 1;
        }
        return amount;
    }
    static isIdenticalTrade(playerTradeState, tradeCreationData) {
        if (playerTradeState.offeredResources.length != tradeCreationData.offeredResources.length) return false;
        if (playerTradeState.wantedResources.length != tradeCreationData.wantedResources.length) return false;
        /*
        * Mau: Trade's Wanted/Offered resources will always be from the perspective of the CURRENT TURN PLAYER, when creating a counter offer, TradeCreationData
        * will be from the perspective of the player who created the original trade, even if the creator is not the current turn player.
        * This is why we need to check if the trade is a counter offer and adjust the comparison accordingly.
        */ const isCounterOffer = tradeCreationData.counterOfferInResponseToTradeId != undefined;
        if (isCounterOffer) {
            if (!ArrayUtils.arraysEqual(playerTradeState.offeredResources, tradeCreationData.wantedResources)) return false;
            if (!ArrayUtils.arraysEqual(playerTradeState.wantedResources, tradeCreationData.offeredResources)) return false;
            return true;
        }
        if (!ArrayUtils.arraysEqual(playerTradeState.offeredResources, tradeCreationData.offeredResources)) return false;
        if (!ArrayUtils.arraysEqual(playerTradeState.wantedResources, tradeCreationData.wantedResources)) return false;
        return true;
    }
    constructor(activeResourceCards, numberOfPlayers, playerCards, isCreatorBot, bankState, playerTradeBankRatioState, activeTrades, tradesClosedThisTurn, modeSetting, embargoState, currentTurnPlayerColors){
        this.activeResourceCards = activeResourceCards;
        this.numberOfPlayers = numberOfPlayers;
        this.playerCards = new CardValidatorOrdered({
            cards: playerCards
        });
        this.currentTurnPlayerColors = currentTurnPlayerColors;
        this.isTradeCreatorBot = isCreatorBot;
        this.bankState = bankState;
        this.playerTradeBankRatioState = playerTradeBankRatioState;
        this.activeTradeStates = activeTrades;
        this.tradesClosedThisTurn = tradesClosedThisTurn;
        this.modeSetting = modeSetting;
        this.embargoState = embargoState;
        this.allValidCards = CardDataController.getCardEnumsOfTypes([
            ...activeResourceCards,
            CardTypeEnum.AnyResource
        ]);
    }
}
TradeCreationDataValidator.maximumNumberOfTimesAnIdenticalTradeCanBeCreatedByBots = 1;
TradeCreationDataValidator.maximumNumberOfTimesAnIdenticalTradeCanBeCreatedForCurrentTurnPlayer = 3;
TradeCreationDataValidator.maximumNumberOfTimesAnIdenticalTradeCanBeCreatedForOtherPlayers = 2;
TradeCreationDataValidator.maximumNumberOfActiveUniqueTradesForCurrentTurnPlayer = 5;
TradeCreationDataValidator.maximumNumberOfActiveUniqueTradesForOtherPlayers = 3;
